/**
 * Zoom Blur — radial Gaussian average centered on (cx, cy).
 *
 * Each pixel samples along the radial ray from (cx, cy) through itself
 * and averages neighbours along that ray using a Gaussian-weighted
 * window. Produces the classic "zooming in" trail (or "explode" from
 * centre depending on whether the user perceives motion as inward or
 * outward).
 *
 * The radial parameterisation is by `amount` ∈ [0, 1], which maps to a
 * fractional scale step δ. The kernel samples at scale factors
 * `1 + k·δ` for k ∈ [−K/2, +K/2], so the trail length is proportional
 * to the pixel's distance from (cx, cy). Pixels at the centre get no
 * blur (zero radial offset → all samples are at the same point).
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * amount ≤ 0 → no-op.
 *
 * References:
 *   • Standard radial-blur math from any DSP textbook (e.g. Gonzalez &
 *     Woods Chapter 4). Common implementation in After Effects, GIMP
 *     UI labels it "Zoom Blur" or "Radial Motion".
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/zoom-blur
 */

import { mirrorIndex, validateRGBA } from './_util.js';

/** Slider ranges. */
export const ZOOM_AMOUNT_MIN = 0;
export const ZOOM_AMOUNT_MAX = 1;

const IDENTITY_EPS = 1e-6;

/* Number of samples along the radial line. Higher = smoother but
 * heavier. 21 is the sweet spot for visible motion without ringing. */
const ZOOM_KERNEL_SAMPLES = 21;

/**
 * Default Zoom Blur parameters. centerX / centerY are in normalized
 * image coords [0, 1]. Identity short-circuit kicks in on amount = 0.
 *
 * @returns {{ centerX: number, centerY: number, amount: number }}
 */
export function defaultParams() {
    return { centerX: 0.5, centerY: 0.5, amount: 0 };
}

/**
 * Identity test.
 *
 * @param {{ amount?: number }} params
 * @returns {boolean}
 */
export function isIdentityZoom(params) {
    if (!params) return true;
    const a = +params.amount;
    if (!Number.isFinite(a)) return true;
    return a <= IDENTITY_EPS;
}

/**
 * Build a 1D Gaussian kernel of fixed length ZOOM_KERNEL_SAMPLES.
 * σ chosen so the kernel decays to ~5% at the ends.
 *
 * @returns {Float32Array}
 */
function buildZoomKernel() {
    const K = ZOOM_KERNEL_SAMPLES;
    const r = (K - 1) >> 1;
    /* σ = K/4 puts 95% of the integral inside [-r, +r]. */
    const sigma = K / 4;
    const inv2s2 = 1 / (2 * sigma * sigma);
    const out = new Float32Array(K);
    let sum = 0;
    for (let i = -r; i <= r; i++) {
        const v = Math.exp(-i * i * inv2s2);
        out[i + r] = v;
        sum += v;
    }
    const inv = 1 / sum;
    for (let i = 0; i < K; i++) out[i] *= inv;
    return out;
}

/**
 * Apply radial Zoom Blur in-place on a packed RGBA Float32 buffer.
 *
 * Algorithm (per pixel at integer (x, y)):
 *   dx, dy = x - cx, y - cy
 *   for k in [-r, +r]:
 *       scale = 1 + k * δ           (δ = amount * 0.5 / r — see below)
 *       sx, sy = cx + dx * scale, cy + dy * scale
 *       accumulate bilinear sample with weight kernel[k+r]
 *
 * The δ scaling: at the corner of the image, distance from centre is
 * ~hypot(w, h)/2 px. We want amount=1 to produce a noticeable trail
 * (a few % zoom). δ = amount * 0.05 gives a 5%-zoom trail at amount=1,
 * which matches the user's expectation of "obvious motion".
 *
 * Boundary handling: mirror-extend on x/y via `mirrorIndex`.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.centerX   normalized [0, 1]
 * @param {number} params.centerY   normalized [0, 1]
 * @param {number} params.amount    [0, 1], 0 = identity
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyZoomBlur(rgba, w, h, params) {
    validateRGBA('applyZoomBlur', rgba, w, h);
    const p = params || {};
    if (isIdentityZoom(p)) return rgba;

    const amount = Math.min(ZOOM_AMOUNT_MAX, Math.max(ZOOM_AMOUNT_MIN, +p.amount));
    const cx     = Math.max(0, Math.min(1, Number.isFinite(p.centerX) ? +p.centerX : 0.5)) * (w - 1);
    const cy     = Math.max(0, Math.min(1, Number.isFinite(p.centerY) ? +p.centerY : 0.5)) * (h - 1);

    const kernel = buildZoomKernel();
    const K = kernel.length;
    const r = (K - 1) >> 1;
    const delta = (amount * 0.05) / Math.max(1, r); /* see header note */

    const src = new Float32Array(rgba);

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const ox = x - cx;
            const oy = y - cy;
            let aR = 0, aG = 0, aB = 0;
            for (let k = -r; k <= r; k++) {
                const s = 1 + k * delta;
                const sx = cx + ox * s;
                const sy = cy + oy * s;
                const x0f = Math.floor(sx);
                const y0f = Math.floor(sy);
                const fx = sx - x0f;
                const fy = sy - y0f;
                const x0 = mirrorIndex(x0f,     w);
                const x1 = mirrorIndex(x0f + 1, w);
                const y0 = mirrorIndex(y0f,     h);
                const y1 = mirrorIndex(y0f + 1, h);
                const i00 = (y0 * w + x0) * 4;
                const i01 = (y0 * w + x1) * 4;
                const i10 = (y1 * w + x0) * 4;
                const i11 = (y1 * w + x1) * 4;
                const wkk = kernel[k + r];
                const u = 1 - fx, v = 1 - fy;
                const w00 = wkk * u * v;
                const w01 = wkk * fx * v;
                const w10 = wkk * u * fy;
                const w11 = wkk * fx * fy;
                aR += src[i00]     * w00 + src[i01]     * w01 + src[i10]     * w10 + src[i11]     * w11;
                aG += src[i00 + 1] * w00 + src[i01 + 1] * w01 + src[i10 + 1] * w10 + src[i11 + 1] * w11;
                aB += src[i00 + 2] * w00 + src[i01 + 2] * w01 + src[i10 + 2] * w10 + src[i11 + 2] * w11;
            }
            const j = (y * w + x) * 4;
            rgba[j]     = aR;
            rgba[j + 1] = aG;
            rgba[j + 2] = aB;
            /* alpha untouched. */
        }
    }
    return rgba;
}
